// --------------------------------------------------------------
// Created On: 2024-07-29
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-06
// Modified By: Zachary Thomas
//
// Copyright 2024 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import useApi from "./useApi";
import { API, CHECK_CART_QUANTITY_DELAY_MILLISECONDS } from "../constants/miscellaneous";
import useReduxDispatch from "src/hooks/useReduxDispatch";
import { setCartQuantity } from "src/redux/features/cartQuantitySlice";
import delayMilliseconds from "../utilities/delayMilliseconds";

// Hook for getting the current cart quantity from the API and then updating the Redux store to reflect the new quantity.
// Returns the quantity that was retrieved from the API. If a quantity couldn't be retrieved, then returns null.
export default async function useRefreshCart(
  openingFunction: () => boolean,
  dependencies: unknown[]
): Promise<number | null> {
  const dispatch = useReduxDispatch();

  // Get the current quantity of cart items from the API.
  let quantity: number | null = null;
  useApi(
    async () => {
      // We add a slight delay to avoid race conditions with adding items to the
      // cart at the same time we are trying to get the current cart quantity.
      await delayMilliseconds(CHECK_CART_QUANTITY_DELAY_MILLISECONDS);
      return openingFunction();
    },
    {
      url: `${API}/cart/quantity`,
      method: "GET",
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody) {
        quantity = responseBody.quantity;
        if (quantity !== null) {
          dispatch(setCartQuantity(quantity));
        }
      }
    },
    dependencies
  );

  return quantity;
}

interface ResponseBody {
  quantity: number;
}
